import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import type { KnowledgeBackendSetting } from "../../api/kbSettingsApi";
import { useAuthedApi } from "../../api/useAuthedApi";
import { PageHeader } from "../../components/Common/PageHeader";
import { WorkspaceCard } from "../../components/Common/WorkspaceCard";
import { usePortalScope } from "@/analytics/PortalScope";

function message(e: unknown): string {
  return e instanceof Error && e.message ? e.message : "Something went wrong";
}

/** Human labels for the backend ids. Keyed by id rather than positional so a
 *  value the server adds later still renders (as its raw id) instead of
 *  showing a blank option. */
const LABELS: Record<string, string> = {
  opensearch: "OpenSearch",
  graph: "Graph DB",
};

const DESCRIPTIONS: Record<string, string> = {
  opensearch: "Passage search over the vector index. Works for every tenant.",
  graph:
    "Cited claims from the knowledge graph. Requires ArcadeDB and documents " +
    "ingested through the graph path — a miss returns nothing rather than " +
    "falling back.",
};

/** Which retrieval backend `search_knowledge` uses, deployment-wide.
 *
 *  Platform-admin only, and the API enforces that independently — this panel
 *  renders the 403 rather than hiding it, because a blank panel is
 *  indistinguishable from a broken one.
 */
export function KbSettings() {
  const api = useAuthedApi();
  const [setting, setSetting] = useState<KnowledgeBackendSetting | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Organization settings is the page area; the menu is portaled out of it (D7).
  const backendMenuScope = usePortalScope("kb_settings");

  const load = useCallback(async () => {
    try {
      setSetting(await api.kbSettings.load());
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const onChange = useCallback(
    async (next: string) => {
      setSaving(true);
      setNotice(null);
      try {
        // The response replaces local state, not the submitted value: the
        // server owns normalisation and the audit fields.
        const saved = await api.kbSettings.save(next);
        setSetting(saved);
        setError(null);
        setNotice(`Retrieval now uses ${LABELS[saved.value] ?? saved.value}.`);
      } catch (e) {
        // Leave `setting` alone so the dropdown snaps back to the value that
        // is actually in effect rather than the one that failed to save.
        setError(message(e));
      } finally {
        setSaving(false);
      }
    },
    [api],
  );

  return (
    <>
      <PageHeader title="KB settings" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <WorkspaceCard interactive={false} data-ph-scope="kb_settings">
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Knowledge base type
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Which backend answers knowledge searches. This applies to the whole
              deployment, not just this organization.
            </Typography>
          </Box>

          <FormControl size="small" sx={{ maxWidth: 320 }} disabled={saving || !setting}>
            <InputLabel id="kb-backend-label">Knowledge_Base_Type</InputLabel>
            <Select
              labelId="kb-backend-label"
              label="Knowledge_Base_Type"
              value={setting?.value ?? ""}
              onChange={(e) => void onChange(e.target.value)}
              SelectDisplayProps={{ "aria-label": "Knowledge base type" }}
              data-ph="open"
              MenuProps={{ slotProps: { list: backendMenuScope } }}
            >
              {(setting?.options ?? []).map((id) => (
                <MenuItem key={id} value={id} data-ph="select">
                  {LABELS[id] ?? id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {setting && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {DESCRIPTIONS[setting.value] ?? ""}
            </Typography>
          )}

          {/* Source line: without it the dropdown is ambiguous about whether
              anyone actually chose this value or it is just the fallback. */}
          {setting && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {setting.source === "database"
                ? `Set by ${setting.updated_by ?? "an admin"}${
                    setting.updated_at
                      ? ` on ${new Date(setting.updated_at).toLocaleDateString()}`
                      : ""
                  }.`
                : "No explicit choice — using the deployment default."}
            </Typography>
          )}
        </Stack>
      </WorkspaceCard>
    </>
  );
}
